// Datos mock de respaldo (solo se usan si la API falla)
import { propertiesData } from '../data/propertiesData';
import { authService } from './auth';

const API_URL = process.env.REACT_APP_API_URL || '/api';

export const api = {
  // Obtener todas las propiedades
  async getProperties() {
    try {
      // Solo intentamos con la API si no estamos en desarrollo con datos mock
      if (process.env.NODE_ENV === 'development' && !process.env.REACT_APP_USE_API) {
        console.log('🔄 Usando datos mock en desarrollo');
        return propertiesData;
      }

      // Intentar con la API real (siempre pública, sin token, para que nunca muestre ocultas)
      const response = await fetch(`${API_URL}/properties`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Cache-Control': 'no-cache'
        }
      });
      
      if (!response.ok) {
        throw new Error(`Error HTTP: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('✅ Propiedades cargadas desde API:', data.length);
      return data;
      
    } catch (error) {
      console.warn('⚠️ Fallback a datos mock:', error.message);
      return propertiesData;
    }
  },

  // Obtener una propiedad por ID
  async getProperty(id) {
    try {
      const response = await fetch(`${API_URL}/properties/${id}`);
      
      if (!response.ok) {
        throw new Error(`Error HTTP: ${response.status}`);
      }
      
      return await response.json();
      
    } catch (error) {
      console.warn('⚠️ Usando datos mock para propiedad:', error.message);
      return propertiesData.find(prop => prop.id === parseInt(id)) || null;
    }
  },

  // CREAR una nueva propiedad
  async createProperty(formData) {
    try {
      const token = authService.getToken();
      
      if (!token) {
        throw new Error('No estás autenticado');
      }

      console.log('📤 Enviando propiedad a la API...');
      
      const response = await fetch(`${API_URL}/properties`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
          // NOTA: No establecemos 'Content-Type' para FormData
        },
        body: formData
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Error al crear la propiedad');
      }
      
      console.log('✅ Propiedad creada exitosamente:', data);
      return { success: true, property: data };
      
    } catch (error) {
      console.error('❌ Error creando propiedad:', error);
      return { success: false, error: error.message };
    }
  },

  // ACTUALIZAR una propiedad existente
  async updateProperty(id, formData) {
    try {
      const token = authService.getToken();
      
      if (!token) {
        throw new Error('No estás autenticado');
      }

      console.log(`📤 Actualizando propiedad ${id}...`);
      
      const response = await fetch(`${API_URL}/properties/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Error al actualizar la propiedad');
      }
      
      console.log('✅ Propiedad actualizada exitosamente:', data);
      return { success: true, property: data };
      
    } catch (error) {
      console.error('❌ Error actualizando propiedad:', error);
      return { success: false, error: error.message };
    }
  },

  // ELIMINAR una propiedad
  async deleteProperty(id) {
    try {
      const token = authService.getToken();
      
      if (!token) {
        throw new Error('No estás autenticado');
      }

      console.log(`🗑️ Eliminando propiedad ${id}...`);
      
      const response = await fetch(`${API_URL}/properties/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Error al eliminar la propiedad');
      }
      
      console.log('✅ Propiedad eliminada exitosamente');
      return { success: true, message: data.message };
      
    } catch (error) {
      console.error('❌ Error eliminando propiedad:', error);
      return { success: false, error: error.message };
    }
  },

  // Obtener la configuración pública del sitio (footer, contacto, redes)
  async getSettings() {
    try {
      const response = await fetch(`${API_URL}/settings`);

      if (!response.ok) {
        throw new Error(`Error HTTP: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.warn('⚠️ No se pudo cargar la configuración del sitio:', error.message);
      return null;
    }
  },

  // Actualizar la configuración del sitio (solo admin)
  async updateSettings(settings) {
    try {
      const token = authService.getToken();

      if (!token) {
        throw new Error('No estás autenticado');
      }

      const response = await fetch(`${API_URL}/settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(settings)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Error al actualizar la configuración');
      }

      return { success: true, settings: data.settings };
    } catch (error) {
      console.error('❌ Error actualizando configuración:', error);
      return { success: false, error: error.message };
    }
  },

  // Subir imágenes (si necesitas una función separada)
  async uploadImages(propertyId, images) {
    try {
      const token = authService.getToken();
      
      if (!token) {
        throw new Error('No estás autenticado');
      }

      const formData = new FormData();
      images.forEach((image, index) => {
        formData.append('images', image);
      });

      const response = await fetch(`${API_URL}/properties/${propertyId}/images`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Error al subir imágenes');
      }
      
      return { success: true, images: data.images };
      
    } catch (error) {
      console.error('❌ Error subiendo imágenes:', error);
      return { success: false, error: error.message };
    }
  }
};

// Función para formatear URLs de imágenes
export const formatImageUrl = (imageUrl) => {
  if (!imageUrl) {
    return '/images/property-placeholder.svg';
  }
  
  // Si ya es una URL completa (http/https), la dejamos igual
  if (imageUrl.startsWith('http')) {
    return imageUrl;
  }
  
  // Si es una ruta local que empieza con /uploads/, necesita la base URL
  if (imageUrl.startsWith('/uploads/')) {
    // En desarrollo, usar localhost:5000
    if (process.env.NODE_ENV === 'development') {
      return `http://localhost:5000${imageUrl}`;
    }
    // En producción, usar la misma URL del sitio
    return imageUrl; // Nginx se encargará de redirigir
  }
  
  // Si empieza con /images/, es local al frontend
  if (imageUrl.startsWith('/images/')) {
    return imageUrl;
  }
  
  // Si es una cadena vacía o undefined, devolver imagen por defecto
  return imageUrl || '/images/property-placeholder.svg';
};